// frontend/src/components/grid/inTransitColumn.js
//
// The Inventory grid's In transit column; its hover explanation is
// InTransitTooltip.jsx.
//
// In transit is inbound supply from two unrelated places, and a merchant who
// doubts the number is really asking which one it came from. Hovering the cell
// answers that:
//
//   Purchase orders     12     <- outstanding on POs (incl. a PO the sales
//   Shopify transfers    3        channel mirrors as a transfer)
//
// The sources only. No total line: the cell the merchant is already hovering
// IS the total, and restating it inside the tooltip only takes up room.
//
// The two parts are stored columns written in the same save as the total
// (`purchase_orders.signals.update_inventory_in_transit`) and the database
// refuses a row where they do not add up to it
// (`inventory_in_transit_is_its_two_parts`), so the breakdown cannot drift
// from the number it explains. Nothing here recomputes the cell.
//
// A part worth zero is omitted rather than shown as "0": on a store with no
// sales channel there are no transfers at all, and a permanent zero row is
// noise. So a number made up entirely of one source shows a single line, and
// that line necessarily equals the cell above it.
//
// Which rows have a breakdown at all is `utils/inTransitBreakdown`.
//
// Shipped as a column factory rather than three loose colDef properties
// because the cell and its explanation must name the SAME quantity: the cell
// renders `field`, the tooltip reads `inventory_in_transit` off the row, and
// a `field` pointed at some other column would put one number above a
// breakdown of another and look entirely reasonable doing it. One call site,
// one unit — `inTransitColumn.test.jsx` holds it together.
//
// Renders on data rows, on Shopify group headers and on the pinned totals row
// alike — all three carry the same three fields (api/views/inventory.py).

import { inTransitBreakdown } from '../../utils/inTransitBreakdown';
import { InTransitTooltip } from './InTransitTooltip';

/**
 * The In transit column: a read-only quantity that explains itself on hover.
 *
 * `tooltipValueGetter` only decides WHETHER the tooltip opens — AG Grid skips
 * an empty one — so it returns `true`, not text: the breakdown is numbers
 * laid out in a grid, which is what `tooltipComponent` is for.
 *
 * That `true` is also what the component receives as `props.value`, which is
 * why neither of them reads a total from there — see inTransitBreakdown.
 */
export function inTransitColDef({ headerName }) {
  return {
    field: 'inventory_in_transit',
    headerName,
    editable: false,
    type: 'numberColumn',
    tooltipComponent: InTransitTooltip,
    tooltipValueGetter: (params) =>
      inTransitBreakdown(params.data) ? true : '',
  };
}
